import React, { useEffect, useMemo } from 'react';
import { Coins } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField, InlineEditText } from '../shared/InlineEditField';
import {
  useReturnPeriod,
  ReturnCell,
  ReturnPeriodHeader,
  totalReturnPct,
  pickPeriodReturn,
} from '../shared/ReturnPeriodControls';
import { UpgradePrompt } from '../modals/UpgradeModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { CRYPTO_ACCOUNT_DEFAULTS } from '../../config/constants';
import { CryptoHolding } from '../../types';

const RETURN_PERIOD_STORAGE_KEY = 'tallyup_crypto_return_period';

interface CryptoWidgetProps {
  onAdd: () => void;
  onUpgradeClick?: () => void;
  variant?: 'card' | 'page';
}

export const CryptoWidget: React.FC<CryptoWidgetProps> = ({
  onAdd,
  onUpgradeClick,
  variant = 'card',
}) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { totalCrypto, updateCryptoHolding, handleDeleteCrypto, handleBatchDelete } = useFinancialData();
  const { visibleCrypto, hasHiddenCrypto, hiddenCryptoCount } = useFinancialCalculations();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit, completeEditText } =
    useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('crypto');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteCrypto(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Crypto', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, visibleCrypto.length, handleBatchDeleteAsync);
  const {
    returnPeriod,
    setPeriod,
    periodMenuOpen,
    setPeriodMenuOpen,
    activePeriod,
  } = useReturnPeriod(RETURN_PERIOD_STORAGE_KEY);

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) editMode.exitEditMode();
  }, [collapsed, editMode]);

  const handleSaveNumeric = async (index: number, field: 'amount' | 'costBasis') => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateCryptoHolding(index, field, value, sheetId);
    }
  };

  const handleSaveText = async (index: number, field: 'symbol') => {
    const { value, isValid } = completeEditText();
    if (isValid && sheetId) {
      await updateCryptoHolding(index, field, value, sheetId);
    } else {
      cancelEdit();
    }
  };

  const isPage = variant === 'page';

  // Group by account (same pattern as InvestmentsWidget)
  const { groups, showGroups } = useMemo(() => {
    const accountSet = new Set(visibleCrypto.map(h => h.account || 'Main'));
    const shouldGroup = accountSet.size >= 2;

    if (!shouldGroup) {
      return {
        groups: [{ account: '', holdings: visibleCrypto.map((h, idx) => ({ h, originalIndex: idx })) }],
        showGroups: false,
      };
    }

    const groupMap = new Map<string, Array<{ h: CryptoHolding; originalIndex: number }>>();
    visibleCrypto.forEach((h, idx) => {
      const acct = h.account || 'Main';
      if (!groupMap.has(acct)) groupMap.set(acct, []);
      groupMap.get(acct)!.push({ h, originalIndex: idx });
    });

    const ordered: Array<{ account: string; holdings: Array<{ h: CryptoHolding; originalIndex: number }> }> = [];
    for (const acctName of CRYPTO_ACCOUNT_DEFAULTS) {
      const items = groupMap.get(acctName);
      if (items) {
        ordered.push({ account: acctName, holdings: items });
        groupMap.delete(acctName);
      }
    }
    Array.from(groupMap.entries()).forEach(([acct, items]) => {
      ordered.push({ account: acct, holdings: items });
    });

    return { groups: ordered, showGroups: true };
  }, [visibleCrypto]);

  // Compute symbol summaries for symbols appearing in 2+ accounts
  const symbolSummaries = useMemo(() => {
    const symbolMap = new Map<string, CryptoHolding[]>();
    groups.forEach((group) => {
      group.holdings.forEach(({ h }) => {
        const sym = h.symbol.toUpperCase();
        if (!symbolMap.has(sym)) symbolMap.set(sym, []);
        symbolMap.get(sym)!.push(h);
      });
    });

    const result: Array<{
      symbol: string;
      totalAmount: number;
      avgCostBasis: number;
      totalValue: number;
      currentPrice: number;
      percentage: number | null;
    }> = [];

    symbolMap.forEach((entries, sym) => {
      if (entries.length < 2) return;
      const totalAmount = entries.reduce((sum, h) => sum + h.amount, 0);
      const totalCost = entries.reduce((sum, h) => sum + h.amount * h.costBasis, 0);
      const avgCostBasis = totalAmount > 0 ? totalCost / totalAmount : 0;
      const totalValue = entries.reduce((sum, h) => sum + h.totalValue, 0);
      const currentPrice = entries[0].currentPrice;
      let percentage: number | null = null;
      if (returnPeriod === 'total') {
        percentage = totalReturnPct(totalCost, totalValue);
      } else {
        percentage =
          entries
            .map(h =>
              pickPeriodReturn(
                returnPeriod,
                {
                  dailyReturn: h.dailyReturn ?? h.change24h,
                  weeklyReturn: h.weeklyReturn,
                  monthlyReturn: h.monthlyReturn,
                  annualReturn: h.annualReturn,
                },
                totalReturnPct(h.amount * h.costBasis, h.totalValue)
              )
            )
            .find(v => v != null) ?? null;
      }
      result.push({ symbol: sym, totalAmount, avgCostBasis, totalValue, currentPrice, percentage });
    });

    return result;
  }, [groups, returnPeriod]);

  const colCount = editMode.isEditMode ? 8 : 7;

  return (
    <Card variant={variant}>
      <CardHeader
        title="Crypto Holdings"
        icon={<Coins size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={visibleCrypto.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('crypto')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
      {editMode.isEditMode && (
        <EditModeActionBar
          selectedCount={editMode.selectedIndices.size}
          totalCount={visibleCrypto.length}
          allSelected={editMode.selectedIndices.size === visibleCrypto.length}
          onSelectAll={() => editMode.selectAll(visibleCrypto.length)}
          onDeselectAll={editMode.deselectAll}
          onDeleteSelected={editMode.deleteSelected}
          isDeleting={editMode.isDeleting}
        />
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm [&_th]:px-2 [&_td]:px-2 md:[&_th]:px-1 md:[&_td]:px-1" style={{ minWidth: 600 }}>
          <thead>
            <tr
              className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${
                darkMode ? 'border-white/10' : 'border-slate-200'
              }`}
            >
              {editMode.isEditMode && <th className="w-8 pb-2" />}
              <th className="text-left pb-2 whitespace-nowrap">Coin</th>
              <th className="text-right pb-2 whitespace-nowrap w-[13%]">Amount</th>
              <th className="text-right pb-2 whitespace-nowrap w-[12%]">Cost</th>
              <th className="text-right pb-2 whitespace-nowrap w-[13%]">Total Cost</th>
              <th className="text-right pb-2 whitespace-nowrap w-[12%]">Price</th>
              <th className="text-right pb-2 whitespace-nowrap w-[13%]">Value</th>
              <ReturnPeriodHeader
                darkMode={darkMode}
                activeShort={activePeriod.short}
                periodMenuOpen={periodMenuOpen}
                returnPeriod={returnPeriod}
                onToggleMenu={() => setPeriodMenuOpen(v => !v)}
                onSelect={setPeriod}
              />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const groupTotal = group.holdings.reduce((sum, { h }) => sum + h.totalValue, 0);
              return (
                <React.Fragment key={group.account || '__flat'}>
                  {showGroups && (
                    <tr className={`${darkMode ? 'bg-white/[0.03]' : 'bg-slate-50/80'}`}>
                      <td
                        colSpan={colCount}
                        className={`py-1.5 px-1 border-l-2 ${darkMode ? 'border-violet-500/40' : 'border-violet-400/50'}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className={`text-sm font-semibold tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {group.account}
                          </span>
                          <span className={`text-sm font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {formatCurrency(groupTotal, privacyMode)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {group.holdings.map(({ h: holding, originalIndex: idx }) => {
                    const totalCost = holding.amount * holding.costBasis;
                    const percentage = pickPeriodReturn(
                      returnPeriod,
                      {
                        dailyReturn: holding.dailyReturn ?? holding.change24h,
                        weeklyReturn: holding.weeklyReturn,
                        monthlyReturn: holding.monthlyReturn,
                        annualReturn: holding.annualReturn,
                      },
                      totalReturnPct(totalCost, holding.totalValue)
                    );

                    return (
                      <tr
                        key={idx}
                        className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${
                          darkMode ? 'border-white/10' : 'border-slate-100'
                        }`}
                      >
                        {editMode.isEditMode && (
                          <td className="py-2 pr-2">
                            <EditModeCheckbox
                              checked={editMode.selectedIndices.has(idx)}
                              onToggle={() => editMode.toggleIndex(idx)}
                            />
                          </td>
                        )}
                        <td className="py-2 font-semibold">
                          <InlineEditText
                            value={holding.symbol}
                            isEditing={isEditing('crypto', idx, 'symbol')}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit('crypto', idx, 'symbol', holding.symbol)}
                            onSave={() => handleSaveText(idx, 'symbol')}
                            onCancel={cancelEdit}
                            editable={holding.editable}
                            width="w-full"
                          />
                        </td>
                        <td className="text-right">
                          <InlineEditField
                            value={holding.amount}
                            isEditing={isEditing('crypto', idx, 'amount')}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit('crypto', idx, 'amount', holding.amount)}
                            onSave={() => handleSaveNumeric(idx, 'amount')}
                            onCancel={cancelEdit}
                            editable={holding.editable}
                            variant="neutral"
                            width="w-full"
                            privacyMode={privacyMode}
                            formatFn={(v, p) => formatNumber(v, 4, p)}
                          />
                        </td>
                        <td className="text-right">
                          <InlineEditField
                            value={holding.costBasis}
                            isEditing={isEditing('crypto', idx, 'costBasis')}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit('crypto', idx, 'costBasis', holding.costBasis)}
                            onSave={() => handleSaveNumeric(idx, 'costBasis')}
                            onCancel={cancelEdit}
                            editable={holding.editable}
                            variant="neutral"
                            width="w-full"
                            privacyMode={privacyMode}
                          />
                        </td>
                        <td className="text-right">
                          {formatCurrency(totalCost, privacyMode)}
                        </td>
                        <td className="text-right">
                          {holding.currentPrice > 0
                            ? formatCurrency(holding.currentPrice, privacyMode)
                            : <span className={darkMode ? 'text-slate-600' : 'text-slate-400'}>--</span>}
                        </td>
                        <td className="text-right font-semibold">
                          {formatCurrency(holding.totalValue, privacyMode)}
                        </td>
                        <td className="text-right">
                          <ReturnCell percentage={percentage} privacyMode={privacyMode} />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {symbolSummaries.length > 0 && (
              <>
                <tr><td colSpan={colCount} className="py-1.5" /></tr>
                {symbolSummaries.map((s) => (
                  <tr key={`summary-${s.symbol}`} className={`${darkMode ? 'bg-blue-500/[0.05]' : 'bg-blue-50/50'} border-l-2 border-blue-500/30`}>
                    {editMode.isEditMode && <td />}
                    <td className={`py-2 pl-1 font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {s.symbol} Total
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatNumber(s.totalAmount, 4, privacyMode)}
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatCurrency(s.avgCostBasis, privacyMode)}
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatCurrency(s.totalAmount * s.avgCostBasis, privacyMode)}
                    </td>
                    <td className={`text-right ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {s.currentPrice > 0
                        ? formatCurrency(s.currentPrice, privacyMode)
                        : <span className={darkMode ? 'text-slate-600' : 'text-slate-400'}>--</span>}
                    </td>
                    <td className={`text-right font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {formatCurrency(s.totalValue, privacyMode)}
                    </td>
                    <td className="text-right">
                      <ReturnCell percentage={s.percentage} privacyMode={privacyMode} />
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>

        {visibleCrypto.length === 0 && (
          <div className={`text-center py-6 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mx-auto mb-2 opacity-50 ${
              darkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600'
            }`}><Coins size={22} /></span>
            <p className="text-sm">No crypto holdings yet</p>
            <p className="text-xs mt-1">Add your first cryptocurrency to start tracking</p>
          </div>
        )}

        {hasHiddenCrypto && onUpgradeClick && (
          <div className="mt-2">
            <UpgradePrompt count={hiddenCryptoCount} type="holdings" onClick={onUpgradeClick} />
          </div>
        )}

      </div>

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm px-2 md:px-1">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Total Crypto
          </span>
          <span className={`${darkMode ? 'text-amber-400' : 'text-amber-600'} text-right`}>
            {formatCurrency(totalCrypto, privacyMode)}
          </span>
        </div>
        <p className={`text-[10px] mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          Prices may be delayed. Crypto is highly volatile and values can change rapidly.
          Data provided by{' '}
          <a
            href="https://www.coingecko.com/en/api"
            target="_blank"
            rel="noopener noreferrer"
            className={`underline ${darkMode ? 'hover:text-slate-400' : 'hover:text-slate-500'}`}
          >
            CoinGecko
          </a>
        </p>
      </CardFooter>
    </Card>
  );
};
